// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import Switch from '@mui/material/Switch';
import { brokerAuthenticationApi as brokerAuth } from '@advisor-app/api/brokers-authentication/broker-authentication-api';
import { Broker } from '@shared/data-access/interfaces/api/brokerage/account';
import { type AccountType } from '@shared/data-access/interfaces/api/brokerage/commons';
import { type FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@advisor-app/store/store';
import { accountApi } from '@advisor-app/store/api/brokerage/accounts-api';
import {
  selectAccountType,
  selectBroker,
  selectBrokerConnected,
  setAccountType,
  setOpenLinkAccountModal,
} from '@advisor-app/store/slices/broker';

export const SwitchAccountType: FC = () => {
  const dispatch = useDispatch();
  const broker = useAppSelector(selectBroker);
  const isBrokerConnected = useAppSelector(selectBrokerConnected);
  const accountType = useAppSelector(selectAccountType);
  const isPaperAccount = useMemo(() => accountType === 'PAPER', [accountType]);
  const isLiveAccount = useMemo(() => accountType === 'LIVE', [accountType]);
  const isInteractiveBrokers = useMemo(
    () => broker === 'interactive_brokers',
    [broker],
  );
  const [postBrokerCredentials] = accountApi.usePostBrokerCredentialsMutation();

  const authenticateWithInteractiveBrokers = useCallback(async () => {
    try {
      const authorizationCode = await brokerAuth.interactive_brokers();
      const response = await postBrokerCredentials({
        brokerKey: Broker.IBKR,
        brokerCredentials: authorizationCode,
      }).unwrap();

      const { connected, type_account } = response;

      if (!connected) {
        throw new Error('Failed to connect to Interactive Brokers');
      }

      dispatch(setAccountType(type_account));
    } catch (error) {
      console.error('Error authenticating with Interactive Brokers', error);
    }
  }, [dispatch, postBrokerCredentials]);

  const handleToggle = useCallback(
    async (accountType?: AccountType) => {
      if (!isBrokerConnected) {
        dispatch(setOpenLinkAccountModal(true));
        return;
      }

      const newAccountType: AccountType =
        accountType || (isPaperAccount ? 'LIVE' : 'PAPER');

      if (accountType === newAccountType) return;
      if (isInteractiveBrokers) {
        await authenticateWithInteractiveBrokers();
        return;
      }

      dispatch(setAccountType(newAccountType));
    },
    [
      authenticateWithInteractiveBrokers,
      dispatch,
      isBrokerConnected,
      isInteractiveBrokers,
      isPaperAccount,
    ],
  );

  return (
    <div
      className="flex cursor-pointer items-center"
      data-testid="switch-account-type"
    >
      <span
        className={`px-2 ${
          isPaperAccount ? 'text-primary-medium' : 'text-control-track'
        }`}
        onClick={() => handleToggle('PAPER')}
      >
        Paper
      </span>
      <Switch
        checked={isLiveAccount}
        onChange={() => handleToggle()}
        color="primary"
        disableRipple
        inputProps={{
          'aria-label': 'controlled',
          role: 'switch',
          'aria-checked': isLiveAccount,
        }}
        className="!h-[19px] !w-[38px] !p-0"
        classes={{
          colorPrimary: '!text-control-thumb',
          track: '!bg-control-track !rounded-full ',
          thumb: '!h-[18px] !w-[18px] box-border ',
          switchBase: '!p-0 !rounded-full !m-0',
        }}
      />
      <span
        className={`px-2 ${
          isLiveAccount ? 'text-primary-medium' : 'text-control-track'
        }`}
        onClick={() => handleToggle('LIVE')}
      >
        Live
      </span>
    </div>
  );
};
